import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { PersonInfo, Subscriber } from 'types/electionService';
import { getManageDependentsForm, INITIAL_FORM_OBJECT } from 'api/operations/formService';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { INITIAL_SUBSCRIBER } from 'store/domain/electionStore';
import { findDependentById } from 'global/services-helpers/electionService';

export default class ManageDependentsStore {
  rootStore: RootStore;
  loading = false;
  isManageDependentsModalOpen = false;
  form: FormObjectFromJSON;
  dynamicForm: null | formUtils.DynamicFormController;
  formId = '';
  private _currentSubscriber: Subscriber = INITIAL_SUBSCRIBER;

  /**
   * Represents the ID of the currently active dependent tab.
   * - If activeDependentId is an empty string, it indicates that the new dependent form is open.
   * - If activeDependentId is null:
   *   - It can be a default value.
   *   - It signifies that there are no dependents and new dependent form is closed.
   */
  activeDependentId: string | null = null;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  /**
   * Method to switch tabs, updating the current active dependent tab and requesting the form ID for the current dependent.
   */
  public setActiveDependentId(activeDependentId: string): void {
    if (this.activeDependentId !== activeDependentId) {
      this.activeDependentId = activeDependentId;
      void this.processGetManageDependentsForm(this.formId, activeDependentId);
    }
  }

  public async openManageDependentsModal(subscriberId: string, formId: string): Promise<void> {
    this.isManageDependentsModalOpen = true;
    this.setLoading(true);
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
    this.formId = formId;

    await this.rootStore.subscriberStore.setCurrentSubscriber(subscriberId);
    runInAction(() => {
      this._currentSubscriber = this.rootStore.subscriberStore.currentSubscriber;

      // Set the first dependent by default if there is any dependent present.
      const defaultDependentId =
        this._currentSubscriber.custom.dependents.length > 0 ? this._currentSubscriber.custom.dependents[0].id : '';
      if (defaultDependentId) this.setActiveDependentId(defaultDependentId);
    });
  }

  private async processGetManageDependentsForm(formId: string, dependentId: string): Promise<void> {
    this.setLoading(true);
    this.form = INITIAL_FORM_OBJECT;
    const sponsorId = this.rootStore.userStore.sponsorId;
    const subscriber = this._currentSubscriber;

    const form = await getManageDependentsForm(
      formId,
      { common: subscriber.common, custom: subscriber.custom as unknown as Record<string, unknown> },
      dependentId || null, // When null, retrieve the form for a new dependent.
      sponsorId,
    );

    runInAction(() => {
      this.form = form;
      const currentDependent = findDependentById(subscriber, dependentId);
      this.createFormController(currentDependent || {});
      this.setLoading(false);
    });
  }

  private createFormController(answers: Record<string, string>): void {
    this.dynamicForm = new formUtils.DynamicFormController(this.form, this.rootStore.userStore.userRoles);
    this.dynamicForm.populateFields(answers);
  }

  public closeManageDependentsModal(): void {
    this.isManageDependentsModalOpen = false;
    this.resetManageDependentForm();
    this.activeDependentId = null;
    this.rootStore.subscriberStore.resetCurrentSubscriber();
  }

  /**
   * Display the new dependent form and create an additional temporary tab before saving.
   */
  public showTabToAddNewDependent(): void {
    this.setActiveDependentId('');
  }

  private resetManageDependentForm(): void {
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
  }

  /**
   * Create a new dependent and open a tab with the newly added existing dependent.
   * Switching to the newly created dependent enables the process of adding dependents and
   * allows for the removal of the current dependent.
   */
  public async addDependent(): Promise<void> {
    this.setLoading(true);
    if (!this.dynamicForm) return;
    const newDependentInfo = { ...this.dynamicForm.getCanonicalAnswers(), id: '' } as unknown as PersonInfo;
    await this.rootStore.subscriberStore.addDependent(newDependentInfo);
    this.resetManageDependentForm();

    const updatedDependentsList = this._currentSubscriber.custom.dependents;
    // New dependents are pushed to the end of the dependents list.
    const newlyAddedDependent = updatedDependentsList[updatedDependentsList.length - 1];

    // Switch to the newly created dependent tab.
    this.setActiveDependentId(newlyAddedDependent?.id || '');
    this.setLoading(false);
  }

  public async updateDependent(dependentId: string): Promise<void> {
    this.setLoading(true);
    if (!this.dynamicForm) return;
    const updatedDependentInfo = {
      ...this.dynamicForm.getCanonicalAnswers(),
      id: dependentId,
    } as unknown as PersonInfo;
    await this.rootStore.subscriberStore.editDependent(updatedDependentInfo);
    this.setLoading(false);
  }

  public removeDependent(dependentId: string): void {
    this.setLoading(true);
    this.rootStore.subscriberStore.removeDependent(dependentId);
    this.activeDependentId = null;
    this.setLoading(false);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
